import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function WeeklyReport() {
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group');
  const startDate = searchParams.get('start');
  const endDate = searchParams.get('end');
  const autoprint = searchParams.get('autoprint') === 'true';
  
  const [receipts, setReceipts] = useState([]);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar información del grupo
        const groupResponse = await axios.get(`${API}/groups`);
        const foundGroup = groupResponse.data.find(g => g.id === groupId);
        setGroup(foundGroup);
        
        // Actualizar título de la página para el PDF
        if (foundGroup) {
          document.title = `Cierre Semanal - ${foundGroup.name}`;
        }

        // Cargar receipts filtrados por fecha
        const receiptsResponse = await axios.get(`${API}/receipts`);
        
        let filteredReceipts = receiptsResponse.data.filter(r => r.group_id === groupId);
        
        // Aplicar filtro de fechas
        if (startDate || endDate) {
          filteredReceipts = filteredReceipts.filter(receipt => {
            const receiptDate = new Date(receipt.date);
            let matchStart = true;
            let matchEnd = true;
            
            if (startDate) {
              const start = new Date(startDate);
              matchStart = receiptDate >= start;
            }
            
            if (endDate) {
              const end = new Date(endDate);
              end.setHours(23, 59, 59, 999);
              matchEnd = receiptDate <= end;
            }
            
            return matchStart && matchEnd;
          });
        }
        
        // Ordenar por fecha descendente
        filteredReceipts.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setReceipts(filteredReceipts);
        setLoading(false);
      } catch (error) {
        console.error('Error loading report data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [groupId, startDate, endDate]);

  // Auto-imprimir si se especificó el parámetro
  useEffect(() => {
    if (!loading && autoprint && receipts.length > 0) {
      // Esperar un poco para que se renderice completamente
      setTimeout(() => {
        window.print();
      }, 1000);
    }
  }, [loading, autoprint, receipts]);

  // Calcular totales
  const totalIngresos = receipts
    .filter(r => r.entry_type === 'ocr' || r.entry_type === 'customer_upload' || r.entry_type === 'manual_credit')
    .reduce((sum, r) => sum + r.amount, 0);
  
  const totalEgresos = receipts
    .filter(r => r.entry_type === 'manual_debit' || r.entry_type === 'manual_payment')
    .reduce((sum, r) => sum + r.amount, 0);
  
  const balanceNeto = totalIngresos - totalEgresos;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { 
      weekday: 'short',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getEntryTypeLabel = (type) => {
    const labels = {
      'ocr': 'Comprobante OCR',
      'customer_upload': 'Subido por Cliente',
      'manual_credit': 'Crédito Manual',
      'manual_debit': 'Débito Manual',
      'manual_payment': 'Pago Manual'
    };
    return labels[type] || 'Desconocido';
  };

  const getReconciliationIcon = (status) => {
    if (status === 'matched') return '🟢';
    if (status === 'partial') return '🟡';
    if (status === 'not_found') return '🔴';
    return '○';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando reporte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header Fijo */}
      <div className="sticky top-0 z-10 bg-white shadow-md border-b-4 border-indigo-600">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cierre Semanal</h1>
              <p className="text-lg text-gray-600 mt-1">{group?.name}</p>
              <p className="text-sm text-gray-500 mt-1">
                Período: {formatDate(startDate)} - {formatDate(endDate)}
              </p>
            </div>
            
            {/* Resumen de Totales */}
            <div className="flex gap-4">
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <p className="text-xs text-green-700 font-medium">INGRESOS</p>
                <p className="text-2xl font-bold text-green-600">${totalIngresos.toLocaleString()}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-xs text-red-700 font-medium">EGRESOS</p>
                <p className="text-2xl font-bold text-red-600">${totalEgresos.toLocaleString()}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <p className="text-xs text-blue-700 font-medium">BALANCE NETO</p>
                <p className="text-2xl font-bold text-blue-600">${balanceNeto.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          {/* Botones de Acción */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimir / PDF
            </button>
            <button
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Contenido Scrolleable */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Estado</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Descripción</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Origen</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">CUIL</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Monto</th>
                </tr>
              </thead>
              <tbody>
                {receipts.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      No hay movimientos en este período
                    </td>
                  </tr>
                ) : (
                  receipts.map((receipt, index) => {
                    const isManual = receipt.entry_type !== 'ocr' && receipt.entry_type !== 'customer_upload';
                    const isIngreso = receipt.entry_type === 'ocr' || receipt.entry_type === 'customer_upload' || receipt.entry_type === 'manual_credit';
                    
                    return (
                      <tr 
                        key={receipt.id} 
                        className={`border-b hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-4 py-3 text-center text-xl">
                          {isManual ? '-' : getReconciliationIcon(receipt.reconciliation_status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(receipt.date)}
                          {receipt.time && (
                            <span className="block text-xs text-gray-500">{receipt.time}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                            isIngreso 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {getEntryTypeLabel(receipt.entry_type)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {isManual ? (receipt.description || '-') : (receipt.transaction_id || '-')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {receipt.origin_holder || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                          {receipt.origin_cuil || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right text-sm font-semibold ${
                          isIngreso ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isIngreso ? '+' : '-'}${receipt.amount.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr>
                  <td colSpan="6" className="px-4 py-4 text-right font-bold text-gray-700">
                    Total de movimientos: {receipts.length}
                  </td>
                  <td className="px-4 py-4 text-right font-bold text-blue-600">
                    ${balanceNeto.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        
        {/* Espacio al final para scroll */}
        <div className="h-20"></div>
      </div>

      {/* Estilos para impresión */}
      <style>{`
        @media print {
          .sticky {
            position: relative;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
}

export default WeeklyReport;
